/**
 * テロップ行ごとの話者割り当て (`scenes.telop_lines`) の FE 表示・編集用ヘルパー
 * (docs/features/scenario-telop-speaker-plan.md §3.7)。
 *
 * 行エディタは DialogueLinesEditor をそのまま流用する。TelopLine と行エディタの行
 * (SpeakerLine) の間は本ファイルのアダプタで変換し、「ナレーション」「話者なし」は
 * `speaker.special` で表す。BE 正本の形は TelopLine (features/scenarios/types.ts)。
 */
import {
  TELOP_LINE_TEXT_MAX_CHARS,
  DIALOGUE_LINE_LABEL_MAX_CHARS,
  type ScenarioCharacter,
  type TelopLine,
  type TelopLineSpeaker,
} from '../../../features/scenarios/types';
import {
  NARRATION_SPEAKER_VALUE,
  NO_SPEAKER_VALUE,
  areDialogueLinesEqual,
  characterSpeakerValue,
  type ExtraSpeakerOption,
  type SpeakerLine,
} from './dialogueLines';
import type { SpeakerLinesEditorLabels, SpeakerLinesEditorLimits } from './sceneItem/DialogueLinesEditor';

export { TELOP_LINE_TEXT_MAX_CHARS };
export type { TelopLine, TelopLineSpeaker, ScenarioCharacter };

/**
 * DB 行 (API レスポンス) の `telop_lines` を UI state 用に正規化する。
 * `[]` (構造化モード・テロップなし) は NULL (レガシーモード) に潰さない (toUiDialogueLines と同じ理由)。
 */
export function toUiTelopLines(raw: TelopLine[] | null | undefined): TelopLine[] | null {
  return raw ?? null;
}

/** シナリオの登場人物宣言 (generation_metadata.characters)。未宣言・旧シナリオは []。 */
export function scenarioCharacters(
  scenario: { generation_metadata?: Record<string, unknown> | null } | null | undefined,
): ScenarioCharacter[] {
  const raw = (scenario?.generation_metadata as { characters?: unknown } | null | undefined)?.characters;
  if (!Array.isArray(raw)) return [];
  return raw.filter(
    (c): c is ScenarioCharacter => !!c && typeof c === 'object' && typeof (c as ScenarioCharacter).key === 'string',
  );
}

/** 行エディタ用: TelopLine → SpeakerLine (ナレーション / 話者なしは special・「合わせる声」(voice_ref) は持ち回る)。 */
export function telopLineToEditorLine(line: TelopLine): SpeakerLine {
  const ref = line.voice_ref && line.voice_ref.length > 0 ? { voice_ref: [...line.voice_ref] } : {};
  const sp = line.speaker;
  if (!sp) {
    return { text: line.text, speaker: { label: '', actor_id: null, actor_kind: null, special: 'none' }, ...ref };
  }
  if (sp.kind === 'narration') {
    return { text: line.text, speaker: { label: 'ナレーション', actor_id: null, actor_kind: null, special: 'narration' }, ...ref };
  }
  return {
    text: line.text,
    speaker: {
      label: sp.label ?? '',
      actor_id: sp.kind === 'actor' ? sp.actor_id : null,
      actor_kind: sp.kind === 'actor' ? sp.actor_kind : null,
      ...(sp.character_key ? { character_key: sp.character_key } : {}),
    },
    ...ref,
  };
}

/** 行エディタ用: SpeakerLine → TelopLine (BE へ送る正本の形)。 */
export function editorLineToTelopLine(line: SpeakerLine): TelopLine {
  const ref = line.voice_ref && line.voice_ref.length > 0 ? { voice_ref: [...line.voice_ref] } : {};
  const sp = line.speaker;
  if (sp.special === 'none') return { text: line.text, speaker: null, ...ref };
  if (sp.special === 'narration') {
    return { text: line.text, speaker: { kind: 'narration', label: null, actor_id: null, actor_kind: null }, ...ref };
  }
  if (sp.actor_id && sp.actor_kind) {
    return { text: line.text, speaker: { kind: 'actor', label: sp.label, actor_id: sp.actor_id, actor_kind: sp.actor_kind }, ...ref };
  }
  return {
    text: line.text,
    speaker: {
      kind: 'other',
      label: sp.label,
      actor_id: null,
      actor_kind: null,
      ...(sp.character_key ? { character_key: sp.character_key } : {}),
    },
    ...ref,
  };
}

export function telopLinesToEditorLines(lines: TelopLine[]): SpeakerLine[] {
  return lines.map(telopLineToEditorLine);
}

export function editorLinesToTelopLines(lines: SpeakerLine[]): TelopLine[] {
  return lines.map(editorLineToTelopLine);
}

/** 「+ 行を追加」の初期値: 話者なし。 */
export function createEmptyTelopEditorLine(): SpeakerLine {
  return { text: '', speaker: { label: '', actor_id: null, actor_kind: null, special: 'none' } };
}

/**
 * 話者ピッカーの追加候補: 登場人物宣言 (character:{key}) → ナレーション → 話者なし。
 * キャスト俳優 (speakerOptions) の後・「自由入力」の前に並ぶ。
 */
export function buildTelopSpeakerExtraOptions(characters: ScenarioCharacter[]): ExtraSpeakerOption[] {
  return [
    ...characters.map((c) => ({ value: characterSpeakerValue(c.key), label: c.label, glyph: 'person' as const })),
    { value: NARRATION_SPEAKER_VALUE, label: 'ナレーション', glyph: 'mic' as const },
    { value: NO_SPEAKER_VALUE, label: '話者なし', glyph: 'none' as const },
  ];
}

/**
 * セリフ (dialogue_lines) の話者ピッカー用の追加候補: 登場人物宣言のみ (ナレーション / 話者なしは
 * セリフには存在しない)。実装後レビュー P2: テロップでは選べるのにセリフでは自由入力しかなく、
 * 同じ名前を入力しても character_key が付かなかった。
 */
export function buildDialogueSpeakerExtraOptions(characters: ScenarioCharacter[]): ExtraSpeakerOption[] {
  return characters.map((c) => ({ value: characterSpeakerValue(c.key), label: c.label, glyph: 'person' as const }));
}

export const TELOP_LINES_EDITOR_LIMITS: SpeakerLinesEditorLimits = {
  textMaxChars: TELOP_LINE_TEXT_MAX_CHARS,
  labelMaxChars: DIALOGUE_LINE_LABEL_MAX_CHARS,
};

export const TELOP_LINES_EDITOR_LABELS: SpeakerLinesEditorLabels = {
  // 1 テロップ内の改行 (同じタイミングで複数行表示) は Shift+Enter。Enter は保存
  textPlaceholder: 'テロップ (Shift+Enter で改行)',
  emptyHint: '（テロップなし）',
};

/** 2 つの telop_lines が同一内容か (外側クリックの破棄確認の dirty 判定)。 */
export function areTelopLinesEqual(a: TelopLine[], b: TelopLine[]): boolean {
  return areDialogueLinesEqual(telopLinesToEditorLines(a), telopLinesToEditorLines(b));
}

/** 編集中のテロップ行 (エディタ形) を破棄して閉じてよいか (shouldDiscardDialogueLinesEdit と同型)。 */
export function shouldDiscardTelopLinesEdit(
  current: SpeakerLine[],
  original: TelopLine[],
  confirmFn: (message: string) => boolean,
): boolean {
  if (areDialogueLinesEqual(current, telopLinesToEditorLines(original))) return true;
  return confirmFn('編集中のテロップを破棄しますか？');
}

/**
 * update_scene の API 返却値から、telop_lines 保存後にローカル state へ反映すべき値を組み立てる
 * (BE は strict 正規化・actor 検証・未宣言 character_key の除去・telop ミラー導出を行う)。
 * 返却値の形が想定外なら送信値へフォールバックする。
 */
export function resolveTelopLinesSaveResult(
  sceneId: string,
  sentLines: TelopLine[],
  response: { id?: string; telop_lines?: TelopLine[] | null; telop?: string } | null | undefined,
): { telop_lines: TelopLine[]; telop: string } {
  const isExpectedShape = !!response && typeof response === 'object' && response.id === sceneId;
  if (!isExpectedShape || !response!.telop_lines) {
    return { telop_lines: sentLines, telop: sentLines.map((l) => l.text).join('\n') };
  }
  return {
    telop_lines: response!.telop_lines,
    telop: response!.telop ?? response!.telop_lines.map((l) => l.text).join('\n'),
  };
}

/** 読み取り表示用: 行の話者の見せ方。 */
export type TelopSpeakerView =
  | { kind: 'none' }
  | { kind: 'narration' }
  | { kind: 'actor'; label: string; actorId: string; actorKind: 'actor' | 'seedance_preset' }
  | { kind: 'label'; label: string };

export function resolveTelopSpeakerView(speaker: TelopLineSpeaker | null): TelopSpeakerView {
  if (!speaker) return { kind: 'none' };
  if (speaker.kind === 'narration') return { kind: 'narration' };
  if (speaker.kind === 'actor' && speaker.actor_id && speaker.actor_kind) {
    return { kind: 'actor', label: speaker.label ?? '', actorId: speaker.actor_id, actorKind: speaker.actor_kind };
  }
  return { kind: 'label', label: speaker.label ?? '' };
}

/**
 * 登場人物の改名/削除 API (`updated_scenes`) をローカル state のシーン配列へ反映する。
 * 返ってきたシーンだけを id で差し替える (dialogue_lines / telop_lines)。
 */
export function applyUpdatedScenesToScenes<T extends { id: string }>(
  scenes: T[],
  updatedScenes: Array<{ id: string; dialogue_lines?: unknown; telop_lines?: unknown }>,
): T[] {
  if (!updatedScenes.length) return scenes;
  const byId = new Map(updatedScenes.map((s) => [s.id, s]));
  return scenes.map((scene) => {
    const upd = byId.get(scene.id);
    if (!upd) return scene;
    return {
      ...scene,
      ...(upd.dialogue_lines !== undefined ? { dialogue_lines: upd.dialogue_lines } : {}),
      ...(upd.telop_lines !== undefined ? { telop_lines: upd.telop_lines } : {}),
    };
  });
}
